
// File: components/engine/GameInstance.ts
import { COLORS } from "../config/colors";
import { CANVAS_WIDTH, CANVAS_HEIGHT, PLAYER_HEIGHT, PLAYER_WIDTH } from "../config/dimensions";
import { drawScene } from "../renderer/drawScene";
import { GroupConfig } from "../../types";
import { InputState } from "../types/common";
import { Ally, AllyProjectile, Enemy, Particle, Player, Projectile } from "../types/entities";
import { handleCollisions } from "./systems/collision";
import { updatePhysics } from "./systems/physics";
import { handleSpawning, spawnEnemy, spawnParticles } from "./systems/spawner";

// Increased from 3 to 10 to match song duration (approx 2-3 mins gameplay)
export const MAX_WAVES = 10;

interface GameInstanceProps {
  playerSquad: GroupConfig;
  producerName: string;
  currentCity: string;
  isPlaneStage?: boolean;
  isBathhouseFight?: boolean;
  isBackgroundOnly?: boolean;
  isPracticeMode?: boolean;
  hideWeapon?: boolean;
  callbacks: {
    onUpdateUI: (score: number, honmoon: number, wave: number, goldenHonmoonTimer: number) => void;
    onGameOver: (finalScore: number) => void;
    onVictory: (finalScore: number) => void;
  }
}

export class GameInstance {
  public player: Player;
  public mira: Ally;
  public zoey: Ally;
  public playerSquad: GroupConfig;
  public producerName: string;
  public currentCity: string;
  public isPlaneStage: boolean;
  public isBathhouseFight: boolean;
  public isBackgroundOnly: boolean;
  public isPracticeMode: boolean;
  public hideWeapon: boolean;
  public projectiles: Projectile[] = [];
  public allyProjectiles: AllyProjectile[] = [];
  public enemies: Enemy[] = [];
  public particles: Particle[] = [];
  public keys: InputState = { ArrowLeft: false, ArrowRight: false, ArrowUp: false, ArrowDown: false, Space: false };
  public lastSpace = false;

  public score = 0;
  public honmoon = 100;
  public wave = 1;
  public planeWave = 0;
  public shake = 0;
  public combo = 0;
  public spawnTimer = 0;
  public enemiesDefeated = 0;
  public goldenHonmoonTimer = 0;
  public achievedGoldenHonmoonThisRun = false;
  public callbacks: GameInstanceProps['callbacks'];
  public isGameOver = false;

  constructor({ playerSquad, producerName, currentCity, isPlaneStage = false, isBathhouseFight = false, isBackgroundOnly = false, isPracticeMode = false, hideWeapon = false, callbacks }: GameInstanceProps) {
    this.playerSquad = playerSquad;
    this.producerName = producerName;
    this.currentCity = currentCity;
    this.isPlaneStage = isPlaneStage;
    this.isBathhouseFight = isBathhouseFight;
    this.isBackgroundOnly = isBackgroundOnly;
    this.isPracticeMode = isPracticeMode;
    this.hideWeapon = hideWeapon || false;
    this.callbacks = callbacks;
    const baseAllyProps = { width: PLAYER_WIDTH * 0.9, height: PLAYER_HEIGHT * 0.9, color: '' };
    this.player = {
      x: CANVAS_WIDTH / 2 - PLAYER_WIDTH / 2,
      y: CANVAS_HEIGHT - PLAYER_HEIGHT - 30,
      width: PLAYER_WIDTH, height: PLAYER_HEIGHT, color: COLORS.player,
      vx: 0, vy: 0, isSwinging: false, swingTimer: 0, facing: 1,
    };
    // SWAPPED: Zoey is now Left, Mira is now Right
    this.zoey = {
      ...baseAllyProps,
      x: this.player.x - PLAYER_WIDTH,
      y: this.player.y,
      attackCooldown: 150,
      targetX: this.player.x - PLAYER_WIDTH,
      isAttacking: false,
      attackTimer: 0
    };
    this.mira = {
      ...baseAllyProps,
      x: this.player.x + PLAYER_WIDTH,
      y: this.player.y,
      attackCooldown: 120,
      targetX: this.player.x + PLAYER_WIDTH,
      isAttacking: false,
      attackTimer: 0
    };
  }

  public enemyDefeated(enemy: Enemy) {
    spawnParticles(this, enemy.x + enemy.width / 2, enemy.y + enemy.height / 2, COLORS.enemy, 20);
    this.enemies = this.enemies.filter(en => en !== enemy);
    this.score += 500;
    this.enemiesDefeated += 1;
    // Don't call updateUI here, let the main loop handle it to avoid excessive calls
  }

  public reset() {
    this.player.x = CANVAS_WIDTH / 2 - PLAYER_WIDTH / 2;
    this.player.vx = 0;
    this.enemies = [];
    this.projectiles = [];
    this.allyProjectiles = [];
    this.particles = [];
    this.score = 0;
    this.honmoon = 100;
    this.wave = 1;
    this.combo = 0;
    this.shake = 0;
    this.spawnTimer = 0;
    this.enemiesDefeated = 0;
    this.goldenHonmoonTimer = 0;
    this.achievedGoldenHonmoonThisRun = false;
    this.isGameOver = false;

    if (this.isPlaneStage) {
      this.planeWave = 1;
      // Plane Stage: Ambush by possessed staff
      // Spawn one large pilot demon and several smaller staff demons.
      const centerX = CANVAS_WIDTH / 2 - PLAYER_WIDTH / 2;
      spawnEnemy(this, centerX, 10, 5); // Pilot Demon (style 5), 10 HP

      spawnEnemy(this, centerX - 200, 3, 7); // Male Staff (style 7)
      spawnEnemy(this, centerX + 200, 3, 6); // Female Staff (style 6)
      spawnEnemy(this, centerX - 100, 2, 7); // Male Staff
      spawnEnemy(this, centerX + 100, 2, 6); // Female Staff
      spawnEnemy(this, centerX - 300, 2, 6); // Female Staff

    } else if (this.isBathhouseFight) {
      // Bathhouse Wave 1: Generic Water Demon Horde (Styles 8, 9, 10)
      this.wave = 1;
      const centerX = CANVAS_WIDTH / 2 - PLAYER_WIDTH / 2;
      const spacing = CANVAS_WIDTH / 4;

      // Helper to randomize
      const randomWaterStyle = () => {
        const r = Math.random();
        if (r < 0.33) return 8; // Standard
        if (r < 0.66) return 9; // Heavy
        return 10; // Swift
      };

      spawnEnemy(this, centerX - spacing, 5, randomWaterStyle());
      spawnEnemy(this, centerX, 5, randomWaterStyle());
      spawnEnemy(this, centerX + spacing, 5, randomWaterStyle());

    } else {
      // Start with Golden Honmoon active immediately for "Golden" song and lyrics
      this.goldenHonmoonTimer = 999999;
      this.achievedGoldenHonmoonThisRun = true;

      // Spawn Wave 1
      const count = 2;
      const styles = [0, 1, 2, 3, 4].sort(() => 0.5 - Math.random());
      const spacing = CANVAS_WIDTH / (count + 1);
      for (let i = 0; i < count; i++) {
        spawnEnemy(this, spacing * (i + 1) - PLAYER_WIDTH / 2, undefined, styles[i]);
      }
    }

    this.callbacks.onUpdateUI(this.score, this.honmoon, this.wave, this.goldenHonmoonTimer);
  }

  public update() {
    if (this.isGameOver) return;
    updatePhysics(this);
    handleSpawning(this);
    handleCollisions(this);
  }

  public draw(ctx: CanvasRenderingContext2D) {
    drawScene(ctx, this);
  }
}
